import React from 'react';
import { useTranslation } from 'react-i18next';
import { Image } from 'antd';
import InfoText from '@/components/atoms/InfoText';
import TitleText from '@/components/atoms/TitleText';
import KsaFlag from '@/assets/icons/KSAFlag';

interface PartyInfo {
  name: string;
  avatar?: string;
  email?: string;
  phone?: string;
}

interface FeedbackPartiesCardsProps {
  seller: PartyInfo;
  buyer: PartyInfo;
  'data-testid'?: string;
}

export const FeedbackPartiesCards: React.FC<FeedbackPartiesCardsProps> = ({
  seller,
  buyer,
  'data-testid': dataTestId = 'feedback-parties-cards-section',
}) => {
  const { t } = useTranslation();

  return (
    <div
      className="grid grid-cols-1 md:grid-cols-2 gap-4"
      data-testid={dataTestId}
    >
      {/* Seller Information Card */}
      <div
        className="py-4 px-6 rounded-xl bg-soft-background dark:bg-dark-surface-primary space-y-4"
        data-testid={`${dataTestId}-seller-card`}
      >
        <TitleText
          text={t('logisticsFeedback.sellerInformation')}
          size="base"
          className="!font-bold !text-light-primary dark:!text-dark-primary"
          data-testid={`${dataTestId}-seller-title`}
        />

        <div
          className="flex items-center gap-3"
          data-testid={`${dataTestId}-seller-profile`}
        >
          <div
            className="w-[50px] h-[50px] rounded-full shrink-0 flex items-center justify-center overflow-hidden border border-light-input-border dark:border-dark-input-border"
            data-testid={`${dataTestId}-seller-avatar`}
          >
            {seller.avatar ? (
              <Image
                src={seller.avatar}
                alt={seller.name}
                width={50}
                height={50}
                className="w-full h-full rounded-full object-cover"
                preview={false}
              />
            ) : (
              <div className="w-full h-full rounded-full bg-soft-background dark:bg-dark-surface-secondary flex items-center justify-center">
                <InfoText
                  text={(seller.name && seller.name !== '-' ? seller.name : 'S')
                    .charAt(0)
                    .toUpperCase()}
                  className="!text-base !font-bold text-light-primary dark:text-dark-primary"
                />
              </div>
            )}
          </div>

          <div className="min-w-0 flex-1 flex flex-col justify-center">
            <InfoText
              text={seller.name || '-'}
              className="!text-base !font-bold !text-light-primary dark:!text-dark-primary truncate leading-tight"
              data-testid={`${dataTestId}-seller-name`}
            />
            {seller.email && (
              <InfoText
                text={seller.email}
                className="!text-sm !font-medium !leading-[22px] !text-light-primary dark:!text-dark-primary truncate mt-0.5"
                data-testid={`${dataTestId}-seller-email`}
              />
            )}
          </div>
        </div>

        {seller.phone && (
          <div
            className="flex items-center gap-2 pt-1"
            data-testid={`${dataTestId}-seller-phone-row`}
          >
            <div className="w-6 h-4 rounded overflow-hidden flex items-center justify-center shrink-0">
              <KsaFlag />
            </div>
            <InfoText
              text={seller.phone}
              className="!text-sm !font-medium !leading-[22px] !text-light-primary dark:!text-dark-primary"
              data-testid={`${dataTestId}-seller-phone-text`}
            />
          </div>
        )}
      </div>

      {/* Buyer Information Card */}
      <div
        className="py-4 px-6 rounded-xl bg-soft-background dark:bg-dark-surface-primary space-y-4"
        data-testid={`${dataTestId}-buyer-card`}
      >
        <TitleText
          text={t('reports.buyerInformation')}
          size="base"
          className="!font-bold !text-light-primary dark:!text-dark-primary"
          data-testid={`${dataTestId}-buyer-title`}
        />

        <div
          className="flex items-center gap-3"
          data-testid={`${dataTestId}-buyer-profile`}
        >
          <div
            className="w-[50px] h-[50px] rounded-full shrink-0 flex items-center justify-center overflow-hidden border border-light-input-border dark:border-dark-input-border"
            data-testid={`${dataTestId}-buyer-avatar`}
          >
            {buyer.avatar ? (
              <Image
                src={buyer.avatar}
                alt={buyer.name}
                width={50}
                height={50}
                className="w-full h-full rounded-full object-cover"
                preview={false}
              />
            ) : (
              <div className="w-full h-full rounded-full bg-soft-background dark:bg-dark-surface-secondary flex items-center justify-center">
                <InfoText
                  text={(buyer.name && buyer.name !== '-' ? buyer.name : 'B')
                    .charAt(0)
                    .toUpperCase()}
                  className="!text-base !font-bold text-light-primary dark:text-dark-primary"
                />
              </div>
            )}
          </div>

          <div className="min-w-0 flex-1 flex flex-col justify-center">
            <InfoText
              text={buyer.name || '-'}
              className="!text-base !font-bold !text-light-primary dark:!text-dark-primary truncate leading-tight"
              data-testid={`${dataTestId}-buyer-name`}
            />
            {buyer.email && (
              <InfoText
                text={buyer.email}
                className="!text-sm !font-medium !leading-[22px] !text-light-primary dark:!text-dark-primary truncate mt-0.5"
                data-testid={`${dataTestId}-buyer-email`}
              />
            )}
          </div>
        </div>

        {buyer.phone && (
          <div
            className="flex items-center gap-2 pt-1"
            data-testid={`${dataTestId}-buyer-phone-row`}
          >
            <div className="w-6 h-4 rounded overflow-hidden flex items-center justify-center shrink-0">
              <KsaFlag />
            </div>
            <InfoText
              text={buyer.phone}
              className="!text-sm !font-medium !leading-[22px] !text-light-primary dark:!text-dark-primary"
              data-testid={`${dataTestId}-buyer-phone-text`}
            />
          </div>
        )}
      </div>
    </div>
  );
};
